import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  LineChart, X, Search, ExternalLink, RefreshCw, Maximize2, 
  TrendingUp, TrendingDown, Minus, Brain, Loader2, Clock,
  Star, StarOff
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { normalizeSymbol } from '../utils/symbolUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Storage key for recent symbols
const RECENT_SYMBOLS_KEY = 'ofs_recent_chart_symbols';
const FAVORITE_SYMBOLS_KEY = 'ofs_favorite_symbols';

// Get recent symbols from localStorage
const getRecentSymbols = () => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_SYMBOLS_KEY) || '[]');
  } catch {
    return [];
  }
};

// Save recent symbol
const saveRecentSymbol = (symbol) => {
  const recent = getRecentSymbols().filter(s => s !== symbol);
  recent.unshift(symbol);
  localStorage.setItem(RECENT_SYMBOLS_KEY, JSON.stringify(recent.slice(0, 10)));
};

// Get favorite symbols
const getFavoriteSymbols = () => {
  try {
    return JSON.parse(localStorage.getItem(FAVORITE_SYMBOLS_KEY) || '[]');
  } catch {
    return [];
  }
};

// Toggle favorite symbol
const toggleFavoriteSymbol = (symbol) => {
  const favorites = getFavoriteSymbols();
  const index = favorites.indexOf(symbol);
  if (index > -1) {
    favorites.splice(index, 1);
  } else {
    favorites.unshift(symbol);
  }
  localStorage.setItem(FAVORITE_SYMBOLS_KEY, JSON.stringify(favorites.slice(0, 20)));
  return favorites;
};

// Global Chart Modal Component
export const GlobalChartModal = ({ isOpen, onClose, initialSymbol = '' }) => {
  const [symbol, setSymbol] = useState(initialSymbol);
  const [searchInput, setSearchInput] = useState(initialSymbol);
  const [isLoading, setIsLoading] = useState(false);
  const [key, setKey] = useState(0);
  const [interval, setInterval] = useState('D');
  const [recentSymbols, setRecentSymbols] = useState([]);
  const [favoriteSymbols, setFavoriteSymbols] = useState([]);
  const [showPatternAnalysis, setShowPatternAnalysis] = useState(false);
  const [patternAnalysis, setPatternAnalysis] = useState(null);
  const [analyzingPatterns, setAnalyzingPatterns] = useState(false);
  const inputRef = useRef(null);

  // Load recent and favorite symbols
  useEffect(() => {
    setRecentSymbols(getRecentSymbols());
    setFavoriteSymbols(getFavoriteSymbols());
  }, [isOpen]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Update when initialSymbol changes
  useEffect(() => {
    if (initialSymbol) {
      // Use centralized normalization
      const cleanSymbol = normalizeSymbol(initialSymbol);
      setSymbol(cleanSymbol);
      setSearchInput(cleanSymbol);
    }
  }, [initialSymbol]);

  const handleSearch = (e) => {
    e?.preventDefault();
    // Use centralized normalization
    const trimmedSymbol = normalizeSymbol(searchInput);
    if (trimmedSymbol) {
      setSymbol(trimmedSymbol);
      setIsLoading(true);
      setKey(prev => prev + 1);
      setPatternAnalysis(null);
      saveRecentSymbol(trimmedSymbol);
      setRecentSymbols(getRecentSymbols());
    }
  };

  const handleQuickSelect = (sym) => {
    // Use centralized normalization
    const cleanSym = normalizeSymbol(sym);
    setSearchInput(cleanSym);
    setSymbol(cleanSym);
    setIsLoading(true);
    setKey(prev => prev + 1);
    setPatternAnalysis(null);
    saveRecentSymbol(cleanSym);
    setRecentSymbols(getRecentSymbols());
  };

  const handleToggleFavorite = (sym) => {
    const updated = toggleFavoriteSymbol(sym);
    setFavoriteSymbols(updated);
    toast.success(updated.includes(sym) ? `Added ${sym} to favorites` : `Removed ${sym} from favorites`);
  };

  // Analyze candlestick patterns
  const analyzeCandlestickPatterns = async () => {
    if (!symbol) return;
    
    setAnalyzingPatterns(true);
    setShowPatternAnalysis(true);
    
    try {
      const response = await axios.post(`${API}/analyze/candlestick-patterns`, {
        symbol: symbol.replace(/.*:/, ''),
        period: '3mo'
      });
      
      if (response.data.success) {
        setPatternAnalysis(response.data);
        toast.success(`Pattern analysis complete for ${symbol}`);
      }
    } catch (error) {
      console.error('Error analyzing patterns:', error);
      toast.error('Failed to analyze candlestick patterns');
      setPatternAnalysis(null);
    } finally {
      setAnalyzingPatterns(false);
    }
  };

  const openFullTradingView = () => {
    window.open(`https://www.tradingview.com/chart/?symbol=${symbol}`, '_blank');
  };

  const popOutChart = () => {
    const widgetUrl = `https://s.tradingview.com/widgetembed/?frameElementId=tradingview_popup&symbol=${encodeURIComponent(symbol)}&interval=${interval}&hidesidetoolbar=0&symboledit=1&saveimage=1&toolbarbg=f1f3f6&studies=%5B%5D&theme=light&style=1&timezone=America%2FNew_York&withdateranges=1&showpopupbutton=1&locale=en`;
    
    const width = 900;
    const height = 650;
    const left = window.screen.width - width - 50;
    const top = 100;
    
    window.open(
      widgetUrl,
      `chart_${symbol}`,
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no,toolbar=no,menubar=no,location=no,status=no`
    );
  };

  const refreshChart = () => {
    setIsLoading(true);
    setKey(prev => prev + 1);
  };

  const getWidgetUrl = () => {
    if (!symbol) return '';
    return `https://s.tradingview.com/widgetembed/?frameElementId=tradingview_chart&symbol=${encodeURIComponent(symbol)}&interval=${interval}&hidesidetoolbar=0&symboledit=1&saveimage=1&toolbarbg=f1f3f6&studies=%5B%5D&theme=light&style=1&timezone=America%2FNew_York&withdateranges=1&showpopupbutton=1&locale=en`;
  };

  const getDirectionDisplay = () => {
    if (!patternAnalysis) return null;
    const dir = patternAnalysis.direction;
    if (dir === 'BULLISH') return { color: 'bg-green-500', text: 'BULLISH 📈' };
    if (dir === 'BEARISH') return { color: 'bg-red-500', text: 'BEARISH 📉' };
    return { color: 'bg-gray-500', text: 'NEUTRAL ⚖️' };
  };

  // Popular symbols
  const popularSymbols = ['SPY', 'QQQ', 'AAPL', 'NVDA', 'TSLA', 'AMZN', 'MSFT', 'META', 'GOOGL', 'AMD'];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] w-[1400px] h-[90vh] p-0 overflow-hidden" data-testid="global-chart-modal">
        <DialogHeader className="px-4 py-3 border-b bg-gradient-to-r from-slate-900 to-indigo-900">
          <div className="flex items-center justify-between w-full gap-4">
            {/* Search Form */}
            <form onSubmit={handleSearch} className="flex items-center space-x-2 flex-1 max-w-md">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  ref={inputRef}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value.toUpperCase())}
                  placeholder="Enter stock symbol (e.g., AAPL, TSLA)"
                  className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                  data-testid="chart-search-input"
                />
              </div>
              <Button type="submit" size="sm" className="bg-indigo-600 hover:bg-indigo-700">
                <Search className="h-4 w-4 mr-1" />
                View
              </Button>
            </form>

            {/* Interval Selector */}
            <div className="flex items-center space-x-1">
              {['1', '5', '15', '60', 'D', 'W', 'M'].map((int) => (
                <Button
                  key={int}
                  variant={interval === int ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => { setInterval(int); setKey(prev => prev + 1); }}
                  className={`text-xs px-2 ${interval === int ? 'bg-indigo-600' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
                >
                  {int === '60' ? '1H' : int}
                </Button>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-2">
              {symbol && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleFavorite(symbol)}
                    className="text-yellow-400 hover:text-yellow-300"
                    title={favoriteSymbols.includes(symbol) ? 'Remove from favorites' : 'Add to favorites'}
                  >
                    {favoriteSymbols.includes(symbol) ? <Star className="h-4 w-4 fill-current" /> : <StarOff className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={analyzeCandlestickPatterns}
                    disabled={analyzingPatterns}
                    className="text-amber-400 hover:text-amber-300"
                    title="AI Pattern Analysis"
                  >
                    <Brain className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={popOutChart}
                    className="text-white/70 hover:text-white"
                    title="Pop out to new window"
                  >
                    <Maximize2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={openFullTradingView}
                    className="text-white/70 hover:text-white"
                    title="Open in TradingView"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={refreshChart}
                    className="text-white/70 hover:text-white"
                    title="Refresh chart"
                  >
                    <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </>
              )}
              <Button variant="ghost" size="sm" onClick={onClose} className="text-white/70 hover:text-white">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex h-[calc(90vh-60px)]">
          {/* Main Chart Area */}
          <div className={`flex-1 flex flex-col ${showPatternAnalysis && patternAnalysis ? 'w-2/3' : 'w-full'} transition-all duration-300`}>
            {/* Quick Access Bar */}
            {!symbol && (
              <div className="p-4 bg-gray-50 border-b">
                {/* Favorites */}
                {favoriteSymbols.length > 0 && (
                  <div className="mb-4">
                    <div className="text-xs font-medium text-gray-500 mb-2 flex items-center">
                      <Star className="h-3 w-3 mr-1 text-yellow-500" /> Favorites
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {favoriteSymbols.map(sym => (
                        <Button
                          key={sym}
                          variant="outline"
                          size="sm"
                          onClick={() => handleQuickSelect(sym)}
                          className="text-xs bg-yellow-50 border-yellow-200 hover:bg-yellow-100"
                        >
                          <Star className="h-3 w-3 mr-1 text-yellow-500 fill-current" />
                          {sym}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent */}
                {recentSymbols.length > 0 && (
                  <div className="mb-4">
                    <div className="text-xs font-medium text-gray-500 mb-2 flex items-center">
                      <Clock className="h-3 w-3 mr-1" /> Recent
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {recentSymbols.map(sym => (
                        <Button
                          key={sym}
                          variant="outline"
                          size="sm"
                          onClick={() => handleQuickSelect(sym)}
                          className="text-xs"
                        >
                          {sym}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Popular */}
                <div>
                  <div className="text-xs font-medium text-gray-500 mb-2 flex items-center">
                    <TrendingUp className="h-3 w-3 mr-1" /> Popular
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {popularSymbols.map(sym => (
                      <Button
                        key={sym}
                        variant="outline"
                        size="sm"
                        onClick={() => handleQuickSelect(sym)}
                        className="text-xs"
                      >
                        {sym}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Chart */}
            {symbol ? (
              <div className="relative flex-1">
                {isLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
                    <div className="text-center">
                      <RefreshCw className="h-8 w-8 animate-spin text-indigo-600 mx-auto mb-2" />
                      <p className="text-gray-600">Loading {symbol} chart...</p>
                    </div>
                  </div>
                )}
                <iframe
                  key={key}
                  src={getWidgetUrl()}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  allowFullScreen
                  onLoad={() => setIsLoading(false)}
                  title={`${symbol} TradingView Chart`}
                />
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <LineChart className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">Enter a stock symbol to view the chart</p>
                  <p className="text-sm">Or select from favorites, recent, or popular stocks above</p>
                </div>
              </div>
            )}
          </div>

          {/* Pattern Analysis Panel */}
          {showPatternAnalysis && symbol && (
            <div className="w-1/3 border-l bg-gradient-to-b from-slate-50 to-white overflow-y-auto">
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold flex items-center">
                    🕯️ Pattern Analysis
                  </h3>
                  <Button variant="ghost" size="sm" onClick={() => setShowPatternAnalysis(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                {analyzingPatterns ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="h-12 w-12 animate-spin text-purple-600 mb-4" />
                    <p className="text-gray-600">Analyzing candlestick patterns...</p>
                  </div>
                ) : patternAnalysis ? (
                  <div className="space-y-4">
                    {/* Direction Badge */}
                    <div className={`p-4 rounded-lg text-center ${
                      patternAnalysis.direction === 'BULLISH' 
                        ? 'bg-green-100 border-2 border-green-300' 
                        : patternAnalysis.direction === 'BEARISH'
                          ? 'bg-red-100 border-2 border-red-300'
                          : 'bg-gray-100 border-2 border-gray-300'
                    }`}>
                      <div className="text-3xl mb-2">
                        {patternAnalysis.direction === 'BULLISH' ? '📈' : patternAnalysis.direction === 'BEARISH' ? '📉' : '⚖️'}
                      </div>
                      <div className={`text-xl font-bold ${
                        patternAnalysis.direction === 'BULLISH' ? 'text-green-700' 
                          : patternAnalysis.direction === 'BEARISH' ? 'text-red-700' : 'text-gray-700'
                      }`}>
                        {patternAnalysis.direction}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        ${patternAnalysis.current_price} | {patternAnalysis.trend}
                      </div>
                    </div>

                    {/* Analysis */}
                    <div className="bg-white rounded-lg border p-4">
                      <h4 className="font-semibold text-purple-700 mb-2 flex items-center">
                        <Brain className="h-4 w-4 mr-2" />
                        AI Analysis
                      </h4>
                      <div className="text-sm text-gray-700 whitespace-pre-wrap">
                        {patternAnalysis.analysis}
                      </div>
                    </div>

                    <Button 
                      onClick={analyzeCandlestickPatterns}
                      className="w-full bg-gradient-to-r from-purple-500 to-indigo-600"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Re-analyze
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Brain className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Click the brain icon to analyze patterns</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Floating Chart Button Component
export const FloatingChartButton = ({ onOpen }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      onClick={onOpen}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`fixed bottom-20 right-4 z-50 flex items-center justify-center rounded-full shadow-lg transition-all duration-300 ${
        isHovered 
          ? 'w-auto px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600' 
          : 'w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-500'
      }`}
      title="Open Live Chart"
      data-testid="floating-chart-button"
    >
      <LineChart className="h-5 w-5 text-white" />
      {isHovered && (
        <span className="ml-2 text-white text-sm font-medium whitespace-nowrap">
          Live Chart
        </span>
      )}
    </button>
  );
};

export default GlobalChartModal;
